import React, { useEffect, useState } from "react";
import {
  Menu,
  Grid,
  Button,
  TextField,
  IconButton,
  FormControl,
  FormHelperText,
  InputAdornment,
  withStyles,
} from "@material-ui/core";
import dayjs from "dayjs";
import { Event } from "@material-ui/icons";

const monthList = [
  { value: "01", name: "Tháng 01" },
  { value: "02", name: "Tháng 02" },
  { value: "03", name: "Tháng 03" },
  { value: "04", name: "Tháng 04" },
  { value: "05", name: "Tháng 05" },
  { value: "06", name: "Tháng 06" },
  { value: "07", name: "Tháng 07" },
  { value: "08", name: "Tháng 08" },
  { value: "09", name: "Tháng 09" },
  { value: "10", name: "Tháng 10" },
  { value: "11", name: "Tháng 11" },
  { value: "12", name: "Tháng 12" },
];

function KPMonthYearPicker(props) {
  const formRef = React.useRef();
  const textareaRef = React.useRef();

  const monthRef = React.useRef();
  const yearRef = React.useRef();

  const { value, setValue } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const StyledMenu = withStyles({
    paper: {
      border: "1px solid #d3d4d5",
      width: Math.max(formRef?.current?.offsetWidth, 320),
    },
  })((props) => (
    <Menu
      elevation={0}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      {...props}
    />
  ));

  // Convert từ định dạng "01/01/2025" hoặc Date object -> "01/2025" (mm/yyyy)
  const renderFormat = (value) => {
    if (!value || value === "mm/yyyy") return "mm/yyyy";

    // Nếu value là Date object, convert sang string
    if (value instanceof Date) {
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const year = value.getFullYear();
      return `${month}/${year}`;
    }

    const dateForm = value?.replaceAll("/", "");
    const month = dateForm?.substr(2, 2);
    const year = dateForm?.substr(-4);
    return `${month}/${year}`;
  };

  // Convert từ định dạng "mm/yyyy" -> "01/mm/yyyy" (ngày 1 của tháng đó)
  const dateFormat = (value) => {
    if (!value || value === "mm/yyyy") return null;
    const parts = value.split("/");
    if (parts.length !== 2) return null;
    const month = parts[0];
    const year = parts[1];
    return `01/${month}/${year}`;
  };

  const handleClick = () => {
    // Khởi tạo giá trị cho month và year khi mở modal
    const displayValue = renderFormat(value);
    const parts = displayValue.split("/");
    let month = parts[0];
    let year = parts[1];

    if (isNaN(parseInt(month))) month = "01";
    if (isNaN(parseInt(year))) year = String(new Date().getFullYear());

    setSelectedMonth(month);
    setSelectedYear(year);
    setAnchorEl(formRef.current);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChange = (e) => {
    // Text to Array
    let charList = e.target.value.split("");
    let cursorIndex = e.target.selectionStart;

    if (charList.length < value.length) {
      const deletedNum = value.length - charList.length;
      charList = value.split("");
      charList[cursorIndex - 1] = e.target.value[cursorIndex - 1];

      const initForm = "mm/yyyy";
      for (let i = cursorIndex; i < cursorIndex + deletedNum; i++) {
        charList[i] = initForm[i];
      }

      setCursorPosition(cursorIndex);
    } else {
      if (charList[cursorIndex] === "/") {
        charList.splice(cursorIndex, 2);
        setCursorPosition(cursorIndex + 1);
      } else {
        charList.splice(cursorIndex, 1);
        setCursorPosition(cursorIndex);
      }
    }

    const newValue = charList.join("");
    setValue(newValue);
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    // Cập nhật giá trị ngay lập tức
    setValue(`01/${newMonth}/${selectedYear}`);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    // Cập nhật giá trị ngay lập tức
    setValue(`01/${selectedMonth}/${newYear}`);
  };

  const handleSubmit = () => {
    // Đảm bảo giá trị được cập nhật trước khi đóng
    setValue(`01/${selectedMonth}/${selectedYear}`);
    handleClose();
  };

  // Giữ modal mở khi selectedMonth hoặc selectedYear thay đổi
  useEffect(() => {
    if (anchorEl && selectedMonth && selectedYear) {
      // Đảm bảo modal không bị đóng khi state thay đổi
      setAnchorEl(formRef.current);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (cursorPosition !== null) textareaRef.current.selectionStart = textareaRef.current.selectionEnd = cursorPosition;
  }, [cursorPosition]);

  // Parse value để lấy month và year
  const displayValue = renderFormat(value);
  const parts = displayValue.split("/");
  let month = parts[0];
  let year = parts[1];

  if (isNaN(parseInt(month))) month = "01";
  if (isNaN(parseInt(year))) year = new Date().getFullYear();

  const yearList = [];
  for (let i = 1900; i <= 2050; i++) yearList.push(i);

  // Validate month/year
  const err =
    value !== "mm/yyyy" &&
    value !== "01/01/2025" &&
    !Boolean(anchorEl) &&
    (parseInt(month) < 1 ||
      parseInt(month) > 12 ||
      parseInt(year) < 1900 ||
      parseInt(year) > 2050 ||
      isNaN(parseInt(month)) ||
      isNaN(parseInt(year)));

  const content = () => {
    return (
      <Grid container spacing={1} style={{ padding: 8 }}>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Tháng"
            variant="outlined"
            value={selectedMonth}
            SelectProps={{ native: true }}
            inputRef={monthRef}
            onChange={handleMonthChange}
          >
            {monthList.map((e) => (
              <option key={`month-${e.value}`} value={e.value}>
                {e.name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Năm"
            variant="outlined"
            value={selectedYear}
            SelectProps={{ native: true }}
            inputRef={yearRef}
            onChange={handleYearChange}
          >
            {yearList.map((e) => (
              <option key={`year-${e}`} value={e}>
                {e}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <Button fullWidth variant="contained" color="primary" onClick={handleSubmit}>
            Xác nhận
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <FormControl error={err} innerRef={formRef} required={props.required} fullWidth={props.fullWidth}>
        <TextField
          fullWidth
          label={props.label}
          inputRef={textareaRef}
          type="tel"
          disabled={props.disabled}
          size={props.size || "small"}
          variant="outlined"
          value={displayValue}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClick} disabled={props.disabled}>
                  <Event />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={handleChange}
        />
        {err && <FormHelperText>Tháng/Năm không hợp lệ</FormHelperText>}
      </FormControl>

      <StyledMenu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        <form onSubmit={handleSubmit}>{content()}</form>
      </StyledMenu>
    </>
  );
}

export default KPMonthYearPicker;

